"use client";

import type { LeadFormFieldChangeHandler } from "./form-types";

import { useCallback, useState } from "react";
import { useLeadFormPersistedValues } from "./LeadFormPersistedValuesContext";

/**
 * Seeds a checkbox field from the machine's persisted values on mount — the
 * mechanism that survives the step region's unmount/remount when it moves
 * into a fullscreen takeover Dialog — and
 * reports every toggle back to the machine through `onFieldChange` as
 * `"on"` / `""`, matching how the server reads `formData.get(fieldName)`.
 */
function usePersistedCheckboxState(
  fieldName: string,
  onFieldChange?: LeadFormFieldChangeHandler,
): [boolean, (checked: boolean) => void] {
  const { persistedFieldValues } = useLeadFormPersistedValues();
  const [checked, setChecked] = useState(
    () => persistedFieldValues[fieldName] === "on",
  );

  const setPersistedChecked = useCallback(
    (nextChecked: boolean) => {
      onFieldChange?.(fieldName, nextChecked ? "on" : "");
      setChecked(nextChecked);
    },
    [fieldName, onFieldChange],
  );

  return [checked, setPersistedChecked];
}

export { usePersistedCheckboxState };
